<template>
  <v-dialog v-model="open" max-width="680" persistent scrollable>
    <v-card class="kf-bulk">
      <v-card-title class="kf-bulk__title">
        <LucideIcon name="upload" :size="20" class="mr-2" />
        Import templates
      </v-card-title>

      <v-card-text class="kf-bulk__body">
        <p class="kf-bulk__lede">
          A complete template is <strong>live</strong> as soon as it is imported. One
          missing something (a DLT id, a registered WhatsApp name, a purpose) is
          saved inactive, and the library says what to fix. A name already in the
          library is updated in place.
        </p>

        <!-- Not a drop zone: the hidden input + button is the pattern the rest
             of the panel already uses (ProductFormDialog's logo picker). -->
        <input
          ref="file_input"
          type="file"
          accept=".csv,.json,text/csv,application/json"
          class="d-none"
          @change="onPicked"
        />
        <div class="kf-bulk__pick">
          <v-btn small outlined class="text-none" @click="$refs.file_input.click()">
            <LucideIcon name="file-up" :size="15" class="mr-1" />
            {{ file_name || "Choose a CSV or JSON file" }}
          </v-btn>
          <v-btn
            v-if="rows.length"
            text
            x-small
            class="text-none ml-1"
            @click="reset"
          >
            Clear
          </v-btn>
        </div>

        <div class="kf-bulk__help">
          <div><strong>CSV</strong> — a header row, then one template per line:</div>
          <code class="kf-code">{{ sampleHeader }}</code>
          <div class="mt-2">
            <code>stage_tags</code> and <code>variables_schema</code> take several
            values separated by <code>|</code>. <strong>JSON</strong> takes a list of
            objects with the same keys.
          </div>
        </div>

        <!-- What was read, before anything is sent. A file that parsed to two
             rows when it should have been two hundred is worth seeing here
             rather than discovering from the result. -->
        <div v-if="parse_error" class="kf-bulk__error">{{ parse_error }}</div>

        <div v-if="rows.length" class="kf-bulk__preview">
          <div class="kf-bulk__count">
            {{ rows.length }} template{{ rows.length === 1 ? "" : "s" }} read from
            {{ file_name }}
          </div>
          <div v-if="nameless" class="kf-bulk__error">
            {{ nameless }} row{{ nameless === 1 ? " has" : "s have" }} no template
            name. Add a column called <code>name</code> (or "Template Name").
          </div>
          <v-simple-table dense class="kf-bulk__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Channel</th>
                <th>Intent</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in preview" :key="i">
                <td class="kf-mono">{{ row.name }}</td>
                <td>{{ row.channel }}</td>
                <td>{{ row.intent }}</td>
                <td class="kf-truncate">{{ row.body }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-if="rows.length > preview.length" class="kf-bulk__more">
            and {{ rows.length - preview.length }} more
          </div>
        </div>

        <!-- Per-row outcomes. A 200-row sheet with two bad rows lands 198 and
             names the two, so the report has to survive the dialog closing. -->
        <div v-if="result" class="kf-bulk__result">
          <div class="kf-bulk__ok">{{ summary(result) }}</div>
          <div v-if="(result.failed || []).length" class="kf-bulk__failed">
            <div class="kf-bulk__failed-head">
              {{ result.failed.length }} row{{ result.failed.length === 1 ? "" : "s" }}
              could not be imported:
            </div>
            <div v-for="(f, i) in result.failed" :key="i" class="kf-bulk__failed-row">
              <span class="kf-mono">row {{ f.row + 1 }}{{ f.name ? " (" + f.name + ")" : "" }}</span>
              — {{ f.error }}
            </div>
          </div>
          <div v-if="(result.warnings || []).length" class="kf-bulk__warn">
            <div v-for="(w, i) in result.warnings" :key="i">{{ w }}</div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="kf-bulk__actions">
        <v-spacer />
        <v-btn text small class="text-none" :disabled="uploading" @click="close">
          {{ result ? "Done" : "Cancel" }}
        </v-btn>
        <v-btn
          v-if="!result"
          color="primary"
          depressed
          small
          class="text-none"
          :disabled="!rows.length"
          :loading="uploading"
          @click="upload"
        >
          Import {{ rows.length || "" }} as drafts
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Bulk import templates from a CSV or JSON file.
 *
 * The file is parsed in the browser and shown back before anything is sent, so
 * a mis-delimited sheet is caught here rather than as 200 server-side failures.
 * The import itself is per-row on the server: good rows land, bad rows come back
 * named, and the dialog stays open on that report rather than closing over it.
 *
 * Multi-value columns use "|" rather than a comma, because a comma inside a CSV
 * cell needs quoting and a spreadsheet export will not reliably produce it.
 */
import api from "@/app-js/karrofin/api";

const LIST_FIELDS = ["stage_tags", "variables_schema"];

// Other spellings a hand-made sheet uses for each column, compared after
// lower-casing and turning spaces/hyphens into "_" ("Template Name" ->
// template_name). Mirrors COLUMN_ALIASES in cashpo-server
// notification/templates.py, which applies the same mapping again; this copy
// only exists so the preview shows what will be imported.
const COLUMN_ALIASES = {
  name: ["template", "template_name", "template_title"],
  body: [
    "message", "text", "content", "message_text", "template_text",
    "template_body", "sms_text", "sms_body", "sms_content",
  ],
  dlt_id: ["dlt", "dlt_template_id", "dlt_template", "dlt_no", "dlt_number"],
  subject: ["title", "heading"],
  stage_tags: ["stage", "stages"],
  variables_schema: ["variables", "placeholders"],
  language: ["lang"],
  whatsapp_template_name: [
    "whatsapp_template", "wa_template", "wa_template_name", "meta_template_name",
  ],
};
const CHANNELS = ["Push", "Whatsapp", "SMS", "Email"];
const PREVIEW_ROWS = 8;

export default {
  name: "TemplateBulkUploadDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      rows: [],
      file_name: "",
      parse_error: "",
      uploading: false,
      result: null,
      sampleHeader:
        "name,intent,channel,language,stage_tags,subject,body,dlt_id," +
        "whatsapp_template_name,variables_schema",
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    preview() {
      return this.rows.slice(0, PREVIEW_ROWS);
    },
    nameless() {
      return this.rows.filter((row) => !row.name).length;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.reset();
    },
  },
  methods: {
    reset() {
      this.rows = [];
      this.file_name = "";
      this.parse_error = "";
      this.result = null;
      if (this.$refs.file_input) this.$refs.file_input.value = "";
    },
    onPicked(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.file_name = file.name;
      this.parse_error = "";
      this.rows = [];
      this.result = null;

      const reader = new FileReader();
      reader.onload = () => {
        try {
          this.rows = file.name.toLowerCase().endsWith(".json")
            ? this.parseJson(String(reader.result))
            : this.parseCsv(String(reader.result));
          if (!this.rows.length) {
            this.parse_error = "No rows found in that file.";
          }
        } catch (e) {
          this.parse_error = "Could not read that file: " + e.message;
        }
      };
      reader.onerror = () => {
        this.parse_error = "Could not read that file.";
      };
      reader.readAsText(file);
    },
    parseJson(text) {
      const data = JSON.parse(text);
      const list = Array.isArray(data) ? data : data.templates;
      if (!Array.isArray(list)) {
        throw new Error("expected a list of templates");
      }
      return list.map((row) => this.normalise(row));
    },
    /**
     * A small CSV reader, quote-aware.
     *
     * Deliberately not a dependency: the panel has no CSV library and the format
     * here is one this screen defines. Handles quoted cells and doubled quotes,
     * which is what a spreadsheet export produces; it does not handle a newline
     * inside a quoted cell, and a body with one would need JSON instead.
     */
    parseCsv(text) {
      const lines = text
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");
      if (!lines.length) return [];

      const header = this.splitLine(lines[0]).map((h) =>
        h.trim().toLowerCase().replace(/\s+/g, "_")
      );
      return lines.slice(1).map((line) => {
        const cells = this.splitLine(line);
        const row = {};
        header.forEach((key, i) => {
          row[key] = (cells[i] || "").trim();
        });
        return this.normalise(row);
      });
    },
    splitLine(line) {
      const out = [];
      let cell = "";
      let quoted = false;
      for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (quoted) {
          if (ch === '"') {
            if (line[i + 1] === '"') {
              cell += '"';
              i += 1;
            } else {
              quoted = false;
            }
          } else {
            cell += ch;
          }
        } else if (ch === '"') {
          quoted = true;
        } else if (ch === ",") {
          out.push(cell);
          cell = "";
        } else {
          cell += ch;
        }
      }
      out.push(cell);
      return out;
    },
    /** Pipe-separated strings become lists; empty cells are dropped entirely. */
    normalise(row) {
      const blank = (v) => v === "" || v === null || v === undefined;
      const named = {};
      Object.keys(row || {}).forEach((key) => {
        const field = String(key).trim().toLowerCase().replace(/[\s-]+/g, "_");
        if (!(field in named)) named[field] = row[key];
      });
      Object.keys(COLUMN_ALIASES).forEach((field) => {
        if (!blank(named[field])) return;
        const alias = COLUMN_ALIASES[field].find((a) => !blank(named[a]));
        if (alias) named[field] = named[alias];
      });
      if (typeof named.channel === "string") {
        const match = CHANNELS.find(
          (c) => c.toLowerCase() === named.channel.trim().toLowerCase()
        );
        if (match) named.channel = match;
      }

      const out = {};
      Object.keys(named).forEach((key) => {
        let value = named[key];
        if (blank(value)) return;
        if (LIST_FIELDS.indexOf(key) !== -1 && typeof value === "string") {
          value = value
            .split("|")
            .map((v) => v.trim())
            .filter(Boolean);
        }
        out[key] = value;
      });
      return out;
    },
    upload() {
      this.uploading = true;
      Promise.resolve()
        .then(() => api.bulkUploadTemplates(this.rows, "bulk upload from " + this.file_name))
        .then((result) => {
          this.result = result;
          // A re-upload of a name already in the library updates it in place,
          // so "updated" is success too.
          if (result.created || result.updated) {
            this.showSnakeBar("success", this.summary(result));
            this.$emit("imported");
          } else if (result.unchanged) {
            this.showSnakeBar("success", this.summary(result));
          } else {
            this.showSnakeBar("error", "Nothing could be imported");
          }
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Could not import the file"
          );
        })
        .finally(() => {
          this.uploading = false;
        });
    },
    summary(result) {
      const drafts = (n) => n + " draft" + (n === 1 ? "" : "s");
      const parts = [drafts(result.created || 0) + " created"];
      if (result.updated) parts.push(drafts(result.updated) + " updated");
      if (result.unchanged) parts.push(result.unchanged + " already up to date");
      return parts.join(", ");
    },
    close() {
      this.open = false;
    },
  },
};
</script>

<style scoped>
.kf-bulk__title {
  font-size: var(--kf-fs-title);
  font-weight: 700;
  padding-bottom: 8px;
}
.kf-bulk__body {
  max-height: 62vh;
}
.kf-bulk__lede {
  font-size: var(--kf-fs-hint);
  color: var(--kf-text-body);
}
.kf-bulk__pick {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.kf-bulk__help {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-bottom: 12px;
}
.kf-code {
  display: block;
  margin-top: 4px;
  padding: 6px 8px;
  background: #F5F5F5;
  border-radius: 4px;
  font-size: var(--kf-fs-label);
  word-break: break-all;
}
.kf-bulk__error {
  font-size: var(--kf-fs-hint);
  color: #C0392B;
  margin-bottom: 10px;
}
.kf-bulk__count {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  margin-bottom: 6px;
}
.kf-bulk__table {
  font-size: var(--kf-fs-label);
}
.kf-mono {
  font-family: monospace;
  font-size: var(--kf-fs-label);
}
.kf-truncate {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kf-bulk__more {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-top: 4px;
}
.kf-bulk__result {
  margin-top: 14px;
  font-size: var(--kf-fs-hint);
}
.kf-bulk__ok {
  font-weight: 600;
  color: #2E7D32;
}
.kf-bulk__failed {
  margin-top: 8px;
  color: #C0392B;
}
.kf-bulk__failed-head {
  font-weight: 600;
}
.kf-bulk__failed-row {
  font-size: var(--kf-fs-label);
  margin-top: 2px;
}
.kf-bulk__warn {
  margin-top: 8px;
  font-size: var(--kf-fs-label);
  color: #6D4C41;
}
.kf-bulk__actions {
  padding: 8px 16px 14px;
}
</style>
